import React, { useState, useEffect, useCallback } from 'react';
import {
  Monitor, Wifi, WifiOff, RefreshCw, Search, Download,
  Printer, CreditCard, MapPin, Clock, Settings, X,
  ChevronDown, ChevronUp, AlertTriangle, MoreVertical, Trash2,
  Send, AlertCircle, CheckCircle, Loader2, Play, Wrench,
  Activity, Bell, Terminal, Power, Eye
} from 'lucide-react';
import api from '../services/api';
import socket from '../services/socket';
import RemoteDesktop from './RemoteDesktop';

export default function DevicesManager({ onClose }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('taquillaId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [sendingCommand, setSendingCommand] = useState(null);
  const [remoteDevice, setRemoteDevice] = useState(null); // Para escritorio remoto

  // Conectar al Panel de Soporte via WebSocket
  useEffect(() => {
    // Unirse a la sala del panel
    socket.emit('join:panel-soporte');

    // Recibir lista inicial de dispositivos conectados
    socket.on('devices:list', (connectedDevices) => {
      console.log('Dispositivos conectados recibidos:', connectedDevices.length);
      updateDevicesWithRealtime(connectedDevices);
    });

    // Dispositivo conectado
    socket.on('device:connected', (deviceInfo) => {
      console.log('Dispositivo conectado:', deviceInfo.taquillaId);
      setDevices(prev => {
        const exists = prev.find(d => d.machineId === deviceInfo.machineId);
        if (exists) {
          return prev.map(d => d.machineId === deviceInfo.machineId ? { ...d, ...deviceInfo, status: 'online' } : d);
        }
        return [...prev, { ...deviceInfo, status: 'online' }];
      });
      showMessage('success', `Taquilla ${deviceInfo.taquillaId} conectada`);
    });

    // Dispositivo desconectado
    socket.on('device:disconnected', (deviceInfo) => {
      console.log('Dispositivo desconectado:', deviceInfo.taquillaId);
      setDevices(prev => prev.map(d =>
        d.machineId === deviceInfo.machineId ? { ...d, status: 'offline', disconnectedAt: deviceInfo.disconnectedAt } : d
      ));
      showMessage('error', `Taquilla ${deviceInfo.taquillaId} desconectada`);
    });

    // Actualizacion de estado de dispositivo
    socket.on('device:status-changed', (deviceInfo) => {
      setDevices(prev => prev.map(d =>
        d.machineId === deviceInfo.machineId ? { ...d, ...deviceInfo } : d
      ));
    });

    // Alerta de dispositivo
    socket.on('device:alert', (alertData) => {
      console.log('Alerta de dispositivo:', alertData);
      setAlerts(prev => [alertData, ...prev].slice(0, 50));
      setShowAlerts(true);
    });

    // Resultado de comando
    socket.on('device:command-result', (result) => {
      console.log('Resultado de comando:', result);
      setSendingCommand(null);
      if (result.success) {
        showMessage('success', `Comando ${result.command} ejecutado correctamente`);
      } else {
        showMessage('error', `Error en comando ${result.command}: ${result.error}`);
      }
    });

    // Cleanup
    return () => {
      socket.off('devices:list');
      socket.off('device:connected');
      socket.off('device:disconnected');
      socket.off('device:status-changed');
      socket.off('device:alert');
      socket.off('device:command-result');
    };
  }, []);

  // Cargar dispositivos desde la base de datos
  useEffect(() => {
    loadDevices();
    loadStats();
  }, []);

  const updateDevicesWithRealtime = (connectedDevices) => {
    setDevices(prev => {
      const updated = [...prev];
      connectedDevices.forEach(cd => {
        const idx = updated.findIndex(d => d.machineId === cd.machineId);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], ...cd, status: 'online' };
        } else {
          updated.push({ ...cd, status: 'online' });
        }
      });
      return updated;
    });
  };

  const loadDevices = async () => {
    try {
      const response = await api.get('/api/devices');
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error cargando dispositivos:', error);
      showMessage('error', 'Error al cargar dispositivos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/devices/meta/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error cargando estadisticas:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDeleteDevice = async (machineId) => {
    if (!window.confirm('¿Eliminar este dispositivo del registro?')) return;

    try {
      await api.delete(`/api/devices/${machineId}`);
      setDevices(devices.filter(d => d.machineId !== machineId));
      showMessage('success', 'Dispositivo eliminado');
    } catch (error) {
      showMessage('error', 'Error al eliminar dispositivo');
    }
  };

  // Enviar comando a dispositivo via WebSocket
  const handleSendCommand = (machineId, command, params = {}) => {
    setSendingCommand({ machineId, command });
    socket.emit('panel:send-command', { machineId, command, params });
    showMessage('success', `Enviando comando "${command}"...`);
  };

  // Solicitar verificacion del sistema
  const handleRequestCheck = (machineId) => {
    socket.emit('panel:request-check', machineId);
    showMessage('success', 'Solicitando verificacion del sistema...');
  };

  const exportToCSV = () => {
    const headers = ['ID Taquilla', 'Lugar', 'Hostname', 'Impresoras', 'Formulario', 'Papel', 'Pinpad', 'Red', 'IP', 'Estado', 'Ultima conexion'];
    const rows = filteredDevices.map(d => [
      d.taquillaId,
      d.lugarNombre || '',
      d.hostname || '',
      d.numImpresoras || 0,
      d.tipoFormulario || '',
      d.tipoPapel || '',
      d.tienePinpad ? 'Si' : 'No',
      d.networkType || '',
      d.staticIp || 'DHCP',
      d.status || '',
      d.lastSeen || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `taquillas_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Calcular estadisticas en tiempo real
  const realtimeStats = {
    total: devices.length,
    online: devices.filter(d => d.status === 'online').length,
    offline: devices.filter(d => d.status !== 'online').length,
    withPinpad: devices.filter(d => d.tienePinpad).length,
    withProblems: devices.filter(d => d.systemStatus && !d.systemStatus.overall).length
  };

  // Filtrar y ordenar dispositivos
  const filteredDevices = devices
    .filter(d => {
      const matchesSearch =
        d.taquillaId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.lugarNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.hostname?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'online' && d.status === 'online') ||
        (filterStatus === 'offline' && d.status !== 'online') ||
        (filterStatus === 'problems' && d.systemStatus && !d.systemStatus.overall);

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const SortHeader = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  // Obtener estado de impresoras del dispositivo
  const getPrinterStatus = (device) => {
    if (!device.systemStatus?.printers) return null;
    return device.systemStatus.printers;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-green-600 to-teal-600 text-white">
          <div className="flex items-center gap-3">
            <Monitor className="w-6 h-6" />
            <div>
              <h2 className="font-bold text-lg">Panel de Taquillas</h2>
              <p className="text-sm text-white/70">
                <span className="inline-flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {realtimeStats.online} online
                </span>
                {' • '}
                {realtimeStats.total} total
                {realtimeStats.withProblems > 0 && (
                  <span className="ml-2 bg-red-500 px-2 py-0.5 rounded text-xs">
                    {realtimeStats.withProblems} con problemas
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Alertas */}
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className={`relative p-2 rounded-lg transition ${alerts.length > 0 ? 'bg-red-500 hover:bg-red-600' : 'hover:bg-white/20'}`}
            >
              <Bell className="w-5 h-5" />
              {alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {alerts.length}
                </span>
              )}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center gap-2 ${
            message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            {message.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Alerts Panel */}
        {showAlerts && alerts.length > 0 && (
          <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-lg max-h-40 overflow-y-auto">
            <div className="p-2 border-b border-red-200 flex items-center justify-between">
              <span className="font-semibold text-red-700 text-sm">Alertas Recientes</span>
              <button onClick={() => setAlerts([])} className="text-xs text-red-600 hover:underline">
                Limpiar
              </button>
            </div>
            <div className="divide-y divide-red-100">
              {alerts.map((alert, i) => (
                <div key={i} className="p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{alert.device?.taquillaId || 'Dispositivo'}</span>
                    <span className="text-gray-500 text-xs">
                      {new Date(alert.timestamp).toLocaleTimeString('es-ES')}
                    </span>
                  </div>
                  <p className="text-red-600 ml-6">{alert.problem?.message || JSON.stringify(alert.problem)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-4 bg-gray-50 border-b">
          <div className="bg-white p-3 rounded-lg border">
            <div className="text-2xl font-bold text-gray-800">{realtimeStats.total}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{realtimeStats.online}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Online
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border">
            <div className="text-2xl font-bold text-gray-400">{realtimeStats.offline}</div>
            <div className="text-xs text-gray-500">Offline</div>
          </div>
          <div className="bg-white p-3 rounded-lg border">
            <div className="text-2xl font-bold text-purple-600">{realtimeStats.withPinpad}</div>
            <div className="text-xs text-gray-500">Con Pinpad</div>
          </div>
          <div className={`bg-white p-3 rounded-lg border ${realtimeStats.withProblems > 0 ? 'border-red-300 bg-red-50' : ''}`}>
            <div className={`text-2xl font-bold ${realtimeStats.withProblems > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {realtimeStats.withProblems}
            </div>
            <div className="text-xs text-gray-500">Con Problemas</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 p-4 border-b">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por ID, lugar, hostname..."
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Todos los estados</option>
            <option value="online">Solo online</option>
            <option value="offline">Solo offline</option>
            <option value="problems">Con problemas</option>
          </select>

          <button
            onClick={loadDevices}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>

          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Monitor className="w-16 h-16 mb-4 opacity-50" />
              <p>No hay taquillas registradas</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <SortHeader field="taquillaId">ID Taquilla</SortHeader>
                  <SortHeader field="lugarNombre">Lugar</SortHeader>
                  <SortHeader field="hostname">Hostname</SortHeader>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Impresoras</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sistema</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pinpad</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Red</th>
                  <SortHeader field="lastSeen">Ultima actividad</SortHeader>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDevices.map(device => {
                  const printerStatus = getPrinterStatus(device);
                  const hasProblems = device.systemStatus && !device.systemStatus.overall;

                  return (
                    <tr key={device.machineId} className={`hover:bg-gray-50 ${hasProblems ? 'bg-red-50' : ''}`}>
                      <td className="px-3 py-2">
                        {device.status === 'online' ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <span className="relative flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                            </span>
                            <span className="text-xs">Online</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <WifiOff className="w-4 h-4" />
                            <span className="text-xs">Offline</span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono font-medium">{device.taquillaId}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          {device.lugarNombre || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{device.hostname || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Printer className={`w-3 h-3 ${printerStatus?.ok === false ? 'text-red-500' : 'text-gray-400'}`} />
                          <span className={printerStatus?.ok === false ? 'text-red-600 font-medium' : ''}>
                            {device.numImpresoras || 0}
                          </span>
                          {printerStatus?.ok === false && (
                            <AlertTriangle className="w-3 h-3 text-red-500" title={printerStatus.issues?.map(i => i.printer).join(', ')} />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {device.systemStatus ? (
                          device.systemStatus.overall ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs">OK</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-xs">Error</span>
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {device.tienePinpad ? (
                          <span className="flex items-center gap-1 text-purple-600">
                            <CreditCard className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          device.networkType === 'static' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'
                        }`}>
                          {device.networkType === 'static' ? device.staticIp : 'DHCP'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {device.lastSeen ? new Date(device.lastSeen).toLocaleString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setRemoteDevice(device)}
                            className="p-1 hover:bg-purple-50 rounded"
                            title="Control remoto"
                            disabled={device.status !== 'online'}
                          >
                            <Eye className={`w-4 h-4 ${device.status === 'online' ? 'text-purple-600' : 'text-gray-300'}`} />
                          </button>
                          <button
                            onClick={() => setSelectedDevice(device)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Ver detalles"
                          >
                            <Settings className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleRequestCheck(device.machineId)}
                            className="p-1 hover:bg-blue-50 rounded"
                            title="Verificar sistema"
                            disabled={device.status !== 'online'}
                          >
                            <Activity className={`w-4 h-4 ${device.status === 'online' ? 'text-blue-500' : 'text-gray-300'}`} />
                          </button>
                          <button
                            onClick={() => handleSendCommand(device.machineId, 'auto-fix-printers')}
                            className="p-1 hover:bg-yellow-50 rounded"
                            title="Auto-reparar impresoras"
                            disabled={device.status !== 'online'}
                          >
                            <Wrench className={`w-4 h-4 ${device.status === 'online' ? 'text-yellow-600' : 'text-gray-300'}`} />
                          </button>
                          <button
                            onClick={() => handleSendCommand(device.machineId, 'restart-app')}
                            className="p-1 hover:bg-red-50 rounded"
                            title="Reiniciar app"
                            disabled={device.status !== 'online'}
                          >
                            <Power className={`w-4 h-4 ${device.status === 'online' ? 'text-red-500' : 'text-gray-300'}`} />
                          </button>
                          <button
                            onClick={() => handleDeleteDevice(device.machineId)}
                            className="p-1 hover:bg-red-50 rounded"
                            title="Eliminar registro"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 text-sm text-gray-500 flex items-center justify-between">
          <span>Mostrando {filteredDevices.length} de {devices.length} taquillas</span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Sincronizacion en tiempo real activa
          </span>
        </div>

        {/* Device Detail Modal */}
        {selectedDevice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div>
                  <h3 className="font-semibold">Taquilla: {selectedDevice.taquillaId}</h3>
                  <p className="text-sm text-gray-500">{selectedDevice.lugarNombre}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedDevice.status === 'online' && (
                    <span className="flex items-center gap-1 text-green-600 text-sm">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Online
                    </span>
                  )}
                  <button onClick={() => setSelectedDevice(null)} className="p-1 hover:bg-gray-200 rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {/* Informacion basica */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Informacion</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
                    <div><span className="text-gray-500">ID:</span> {selectedDevice.taquillaId}</div>
                    <div><span className="text-gray-500">Lugar:</span> {selectedDevice.lugarNombre || '-'}</div>
                    <div><span className="text-gray-500">Hostname:</span> {selectedDevice.hostname}</div>
                    <div><span className="text-gray-500">Version:</span> {selectedDevice.appVersion || '-'}</div>
                  </div>
                </div>

                {/* Impresoras */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Printer className="w-4 h-4" />
                    Impresoras ({selectedDevice.numImpresoras || 0})
                  </h4>
                  {selectedDevice.systemStatus?.printers?.issues?.length > 0 ? (
                    <div className="bg-red-50 p-3 rounded-lg">
                      {selectedDevice.systemStatus.printers.issues.map((issue, i) => (
                        <div key={i} className="text-sm mb-2">
                          <div className="font-medium text-red-700">{issue.printer}</div>
                          <ul className="list-disc list-inside text-red-600">
                            {issue.problems.map((p, j) => <li key={j}>{p}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Todas las impresoras configuradas correctamente
                    </div>
                  )}
                </div>

                {/* Configuracion */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Configuracion</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
                    <div><span className="text-gray-500">Papel:</span> {selectedDevice.tipoPapel || '-'}</div>
                    <div><span className="text-gray-500">Formulario:</span> {selectedDevice.tipoFormulario || '-'}</div>
                    <div><span className="text-gray-500">Pinpad:</span> {selectedDevice.tienePinpad ? (selectedDevice.pinpadModelo || 'Si') : 'No'}</div>
                    <div><span className="text-gray-500">Red:</span> {selectedDevice.networkType === 'static' ? selectedDevice.staticIp : 'DHCP'}</div>
                  </div>
                </div>

                {/* Acciones rapidas */}
                {selectedDevice.status === 'online' && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Acciones</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleRequestCheck(selectedDevice.machineId)}
                        className="flex items-center justify-center gap-2 p-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm"
                      >
                        <Activity className="w-4 h-4" />
                        Verificar Sistema
                      </button>
                      <button
                        onClick={() => handleSendCommand(selectedDevice.machineId, 'auto-fix-printers')}
                        className="flex items-center justify-center gap-2 p-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 text-sm"
                      >
                        <Wrench className="w-4 h-4" />
                        Reparar Impresoras
                      </button>
                      <button
                        onClick={() => handleSendCommand(selectedDevice.machineId, 'check-system')}
                        className="flex items-center justify-center gap-2 p-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Check Completo
                      </button>
                      <button
                        onClick={() => handleSendCommand(selectedDevice.machineId, 'restart-app')}
                        className="flex items-center justify-center gap-2 p-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm"
                      >
                        <Power className="w-4 h-4" />
                        Reiniciar App
                      </button>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-gray-400 border-t pt-2 mt-2">
                  <div>Machine ID: {selectedDevice.machineId?.substring(0, 20)}...</div>
                  {selectedDevice.registeredAt && (
                    <div>Registrado: {new Date(selectedDevice.registeredAt).toLocaleString('es-ES')}</div>
                  )}
                  {selectedDevice.lastSeen && (
                    <div>Ultima actividad: {new Date(selectedDevice.lastSeen).toLocaleString('es-ES')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remote Desktop Modal */}
        {remoteDevice && (
          <RemoteDesktop
            device={remoteDevice}
            onClose={() => setRemoteDevice(null)}
          />
        )}
      </div>
    </div>
  );
}
